import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Briefcase, MapPin, Clock, ArrowRight, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/careers/jobs`);
        setJobs(res.data?.jobs || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/30">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-fredoka text-2xl font-bold text-emerald-600">Zazu</Link>
          <div className="flex gap-4 text-sm">
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
            <Link to="/careers" className="text-emerald-600 font-semibold">Careers</Link>
            <Link to="/login" className="text-slate-600 hover:text-slate-900">Log In</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-16 pb-10 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge className="bg-emerald-100 text-emerald-700 mb-4 px-3 py-1">
            <Sparkles className="w-3 h-3 mr-1" /> Now Hiring · Fall 2026
          </Badge>
          <h1 className="font-fredoka text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            Build the future of<br />K-8 learning with us
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
            All positions are <span className="font-semibold text-slate-800">internships</span> — unpaid, but you&apos;ll earn academic credit, real product experience, mentorship from the founding team, and the chance to grow with a startup building something that matters.
          </p>
        </motion.div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-600">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> ~10-15 hrs/week</span>
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Fully Remote</span>
          <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> Academic Credit + Growth Path</span>
        </div>
      </section>

      {/* Job Listings */}
      <section className="max-w-5xl mx-auto px-4 pb-24">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-emerald-500" /></div>
        ) : jobs.length === 0 ? (
          <p className="text-center text-slate-500 py-16">No open positions right now. Check back soon.</p>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job, i) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={`/careers/${job.slug || job.id}`} data-testid={`job-card-${job.slug}`}>
                  <Card className="hover:shadow-lg hover:border-emerald-300 transition-all group cursor-pointer border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{job.department}</Badge>
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Unpaid Internship</Badge>
                          </div>
                          <h3 className="font-fredoka text-2xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                            {job.title}
                          </h3>
                          <p className="text-slate-600 text-sm mt-2 line-clamp-2">{job.summary}</p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            {(job.skills || []).slice(0, 4).map((s) => (
                              <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{s}</span>
                            ))}
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Why Zazu */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-fredoka text-3xl font-bold mb-8 text-center">What you&apos;ll get</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Real Impact', body: 'Ship features to thousands of real kids and parents — no throwaway coursework.' },
              { title: 'Founder Mentorship', body: 'Weekly 1:1s with the founding team. Learn how a startup ships fast.' },
              { title: 'Growth Runway', body: 'Prove yourself and grow into a paid full-time role as Zazu scales.' },
              { title: 'Academic Credit', body: 'Formal internship credit letter for your school. We can partner with your program.' },
              { title: 'Portfolio-Worthy', body: 'Every project is shippable, measurable, and shareable on LinkedIn.' },
              { title: 'Flexible Schedule', body: '10-15 hrs/week async — perfect around your class schedule.' },
            ].map((perk) => (
              <div key={perk.title} className="bg-white/5 rounded-xl p-5 border border-white/10">
                <h3 className="font-semibold text-lg mb-1 text-emerald-300">{perk.title}</h3>
                <p className="text-sm text-slate-300">{perk.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
